import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Mail, Send, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Bitte geben Sie eine gültige E-Mail-Adresse ein');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      
      setSuccess(true);
      toast.success('Nachricht erfolgreich gesendet!');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });

      // Reset success state after 5 seconds
      setTimeout(() => setSuccess(false), 5000);
    } catch (error) {
      console.error('Contact form error:', error);
      const errorMsg = error.response?.data?.detail || 'Fehler beim Senden der Nachricht';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-3" style={{ color: '#E6EDF3' }}>
            Support kontaktieren
          </h1>
          <p className="text-lg" style={{ color: '#94A3B8' }}>
            Haben Sie Fragen oder benötigen Hilfe? Wir sind für Sie da!
          </p>
        </div>

        {success ? (
          // Success Message
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#22C55E' }}>
            <CardContent className="py-12">
              <div className="text-center">
                <div 
                  className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
                  style={{ backgroundColor: '#22C55E20' }}
                >
                  <CheckCircle2 className="w-10 h-10" style={{ color: '#22C55E' }} />
                </div>
                <h2 className="text-2xl font-bold mb-3" style={{ color: '#E6EDF3' }}>
                  Nachricht gesendet!
                </h2>
                <p style={{ color: '#94A3B8' }}>
                  Vielen Dank für Ihre Nachricht. Wir werden uns schnellstmöglich bei Ihnen melden.
                </p>
                <Button
                  onClick={() => setSuccess(false)}
                  className="mt-6"
                  style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
                >
                  Weitere Nachricht senden
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          // Contact Form
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardHeader>
              <CardTitle style={{ color: '#E6EDF3' }}>
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: '#7CD4FD20' }}
                  >
                    <Mail className="w-6 h-6" style={{ color: '#7CD4FD' }} />
                  </div>
                  <span>Kontaktformular</span>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div>
                  <Label style={{ color: '#E6EDF3', marginBottom: '8px', display: 'block' }}>
                    Name *
                  </Label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Ihr Name"
                    required
                    style={{ 
                      backgroundColor: '#1F2937', 
                      borderColor: '#374151', 
                      color: '#E6EDF3',
                      padding: '12px'
                    }}
                  />
                </div>

                {/* Email */}
                <div>
                  <Label style={{ color: '#E6EDF3', marginBottom: '8px', display: 'block' }}>
                    E-Mail-Adresse *
                  </Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="ihre@email.com"
                    required
                    style={{ 
                      backgroundColor: '#1F2937', 
                      borderColor: '#374151', 
                      color: '#E6EDF3',
                      padding: '12px'
                    }}
                  />
                </div>

                {/* Subject */}
                <div>
                  <Label style={{ color: '#E6EDF3', marginBottom: '8px', display: 'block' }}>
                    Betreff
                  </Label>
                  <Input
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Worum geht es?"
                    style={{ 
                      backgroundColor: '#1F2937', 
                      borderColor: '#374151', 
                      color: '#E6EDF3',
                      padding: '12px'
                    }}
                  />
                </div>

                {/* Message */}
                <div>
                  <Label style={{ color: '#E6EDF3', marginBottom: '8px', display: 'block' }}>
                    Nachricht *
                  </Label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Ihre Nachricht..."
                    rows={8}
                    required
                    style={{ 
                      backgroundColor: '#1F2937', 
                      borderColor: '#374151', 
                      color: '#E6EDF3',
                      padding: '12px'
                    }}
                  />
                  <p className="text-xs mt-2" style={{ color: '#6B7280' }}>
                    Mindestens 10 Zeichen
                  </p>
                </div>

                {/* Info Box */}
                <div 
                  className="flex items-start gap-3 p-4 rounded-lg"
                  style={{ backgroundColor: '#7CD4FD20', border: '1px solid #7CD4FD' }}
                >
                  <AlertCircle className="w-5 h-5 mt-0.5" style={{ color: '#7CD4FD' }} />
                  <div>
                    <p className="text-sm font-semibold" style={{ color: '#7CD4FD' }}>
                      Antwortzeit
                    </p>
                    <p className="text-sm mt-1" style={{ color: '#E6EDF3' }}>
                      Wir antworten in der Regel innerhalb von 24 Stunden
                    </p>
                  </div>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full py-6 rounded-xl font-bold text-lg"
                  style={{
                    background: loading ? '#374151' : 'linear-gradient(135deg, #7CD4FD 0%, #1EA0E6 100%)',
                    color: '#fff',
                    opacity: loading ? 0.6 : 1
                  }}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Wird gesendet...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Nachricht senden
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Contact Info */}
        <div className="mt-8 text-center">
          <p className="text-sm" style={{ color: '#6B7280' }}>
            Alternativ erreichen Sie uns auch per E-Mail oder Telefon
          </p>
        </div>
      </div>
    </div>
  );
};
